import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from '../../../campaigns/hooks/useToast'
import { testSendSettings, testReceiveSettings } from '../services/B2BServices'

export const useEmailSettingsTests = (connectionType, userEditState, watchedValues) => {
  const { t } = useTranslation()
  const { showSuccess, showError } = useToast()
  const [testingSender, setTestingSender] = useState(false)
  const [testingReceiver, setTestingReceiver] = useState(false)

  const isTestSenderDisabled = useMemo(() => {
    return testingSender ||
      !watchedValues?.emailUserName?.trim() ||
      !watchedValues?.emailPassword?.trim() ||
      !watchedValues?.smtpHostName?.trim() ||
      !watchedValues?.smtpPort?.toString()?.trim() ||
      !watchedValues?.transportStrategy?.trim()
  }, [
    testingSender,
    watchedValues?.emailUserName,
    watchedValues?.emailPassword,
    watchedValues?.smtpHostName,
    watchedValues?.smtpPort,
    watchedValues?.transportStrategy
  ])

  const isTestReceiverDisabled = useMemo(() => {
    return testingReceiver ||
      !watchedValues?.emailUserName?.trim() ||
      !watchedValues?.emailPassword?.trim() ||
      !watchedValues?.rxHostName?.trim() ||
      !watchedValues?.rxPort?.toString()?.trim() ||
      !watchedValues?.rxProtocol?.trim() ||
      !watchedValues?.inboxFolderName?.trim()
  }, [
    testingReceiver,
    watchedValues?.emailUserName,
    watchedValues?.emailPassword,
    watchedValues?.rxHostName,
    watchedValues?.rxPort,
    watchedValues?.rxProtocol,
    watchedValues?.inboxFolderName
  ])

  const buildEmailSettings = useCallback((val) => {
    const accountType = connectionType === 'gmail' ? 'GMAIL_OAUTH2' : 'USERNAME_PASSWORD'
    return {
      accountType,
      emailUserName: val.emailUserName,
      emailPassword: val.emailPassword,
      smtpHostName: val.smtpHostName,
      smtpPort: Number(val.smtpPort),
      transportStrategy: val.transportStrategy,
      rxHostName: val.rxHostName,
      rxPort: Number(val.rxPort),
      rxProtocol: val.rxProtocol,
      inboxFolderName: val.inboxFolderName,
      accessToken: userEditState?.emailSettings?.accessToken || null,
      refreshToken: userEditState?.emailSettings?.refreshToken || null,
      expireTimeInSeconds: userEditState?.emailSettings?.expireTimeInSeconds || null
    }
  }, [connectionType, userEditState?.emailSettings])

  const testSenderSettings = useCallback((val) => {
    const emailSettings = buildEmailSettings(val)
    setTestingSender(true)
    testSendSettings(emailSettings).then(() => {
      showSuccess(t('mailTemplate.users.modal.senderTestSuccess'))
    }).catch(() => {
      showError(t('mailTemplate.users.modal.senderTestFailed'))
    }).finally(() => {
      setTestingSender(false)
    })
  }, [buildEmailSettings, showSuccess, showError, t])

  const testReceiverSettings = useCallback((val) => {
    const emailSettings = buildEmailSettings(val)
    setTestingReceiver(true)
    testReceiveSettings(emailSettings).then(() => {
      showSuccess(t('mailTemplate.users.modal.receiverTestSuccess'))
    }).catch(() => {
      showError(t('mailTemplate.users.modal.receiverTestFailed'))
    }).finally(() => {
      setTestingReceiver(false)
    })
  }, [buildEmailSettings, showSuccess, showError, t])

  return {
    testingSender,
    testingReceiver,
    isTestSenderDisabled,
    isTestReceiverDisabled,
    testSenderSettings,
    testReceiverSettings
  }
}
